import { zodResolver } from '@hookform/resolvers/zod';
import { type Property, type TimesheetDepartment, type User } from '@kalos/kalos-rpc';
import {
  Badge,
  Button,
  Form as FormContext,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kalos/ui';
import { InfoCircledIcon, TrashIcon } from '@radix-ui/react-icons';
import { parseISO } from 'date-fns';
import { useMemo, useState } from 'react';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Flagged } from '../../../../featureFlags/Flagged';
import { useFeatureFlag } from '../../../../featureFlags/useFeatureFlag';
import { useTimesheetDepartmentListQuery } from '../../../../hooks/react-query/useTimesheetDepartmentQuery';
import { EmployeeComboboxPicker } from '../../../ComponentsLibrary/Pickers/newPickers/QueryPickerV2';
import { useSchedulerStyle } from '../context/schedulerStyle';
import { formatDayRange } from '../utils/multiDay';

// -------------------- Types & Schema --------------------
export interface ProjectDetailsProps {
  customer: User;
  property: Property;
  onBack: () => void;
  onSubmit: (data: ProjectFormValues) => void;
  onCancel?: () => void;
  hideCancelButton?: boolean;
  loading?: boolean;
}

export interface DepartmentAssignment {
  departmentId: number;
  departmentName: string;
  isPrime: boolean;
  allocatedBudget: number;
  allocatedHours: number;
}

const projectSchema = z
  .object({
    dateStarted: z.string().min(1, 'Start date is required'),
    dateEnded: z.string().min(1, 'End date is required'),
    poNumber: z.string().optional(),
    description: z.string().min(1, 'Project description is required'),
    logNotes: z.string().optional(),
    projectManagerId: z.number().optional(),
    departmentAssignments: z
      .array(
        z.object({
          departmentId: z.number(),
          departmentName: z.string(),
          isPrime: z.boolean(),
          allocatedBudget: z.number().min(0).default(0),
          allocatedHours: z.number().min(0).default(0),
        }),
      )
      .min(1, 'At least one department must be selected'),
  })
  .refine(
    (data) => {
      if (data.dateStarted && data.dateEnded) {
        return data.dateEnded > data.dateStarted;
      }
      return true;
    },
    {
      message: 'End date must be after start date',
      path: ['dateEnded'],
    },
  );

export type ProjectFormValues = z.infer<typeof projectSchema>;

// Helper function to create required field labels with red asterisk
const RequiredLabel = ({ children }: { children: React.ReactNode }) => (
  <FormLabel>
    {children} <span className="text-red-500">*</span>
  </FormLabel>
);

// -------------------- Component --------------------
export const ProjectDetails = ({
  customer,
  property,
  onBack,
  onSubmit,
  onCancel,
  hideCancelButton = false,
  loading = false,
}: ProjectDetailsProps) => {
  const compact = useSchedulerStyle();
  const multiDayFlag = useFeatureFlag('job-creation.multi-day');
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);

  // Fetch departments
  const { data: departmentsData } = useTimesheetDepartmentListQuery({
    filter: { isActive: 1, teamOnly: false, fieldMask: ['TeamOnly'] },
  });

  const departments = useMemo<TimesheetDepartment[]>(
    () => departmentsData?.results ?? [],
    [departmentsData?.results],
  );

  // Get tomorrow's date as default end date
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectSchema),
    mode: 'onChange',
    defaultValues: {
      dateStarted: today.toISOString().split('T')[0],
      dateEnded: tomorrow.toISOString().split('T')[0],
      poNumber: '',
      description: '',
      logNotes: '',
      projectManagerId: undefined,
      departmentAssignments: [],
    },
  });

  const departmentAssignments = form.watch('departmentAssignments');
  const projectDateStarted = form.watch('dateStarted');
  const projectDateEnded = form.watch('dateEnded');
  // Shown only for a valid range; an end on or before the start has its own error.
  const projectDayRange =
    projectDateStarted && projectDateEnded && projectDateEnded > projectDateStarted
      ? formatDayRange(parseISO(projectDateStarted), parseISO(projectDateEnded))
      : null;

  // Get list of already selected department IDs
  const selectedDepartmentIds = useMemo(() => {
    return new Set(departmentAssignments.map((d) => d.departmentId));
  }, [departmentAssignments]);

  // Available departments (not yet selected)
  const availableDepartments = useMemo(() => {
    return departments.filter((dept) => !selectedDepartmentIds.has(dept.id));
  }, [departments, selectedDepartmentIds]);

  const handleAddDepartment = () => {
    if (selectedDepartmentId === 0) return;

    const dept = departments.find((d) => d.id === selectedDepartmentId);
    if (!dept) return;

    const currentAssignments = form.getValues('departmentAssignments') || [];
    const isFirstDepartment = currentAssignments.length === 0;

    form.setValue(
      'departmentAssignments',
      [
        ...currentAssignments,
        {
          departmentId: dept.id,
          departmentName: dept.description || `Department ${dept.id}`,
          isPrime: isFirstDepartment, // First department is automatically prime
          allocatedBudget: 0,
          allocatedHours: 0,
        },
      ],
      { shouldValidate: true },
    );

    setSelectedDepartmentId(0);
  };

  const handleRemoveDepartment = (index: number) => {
    const currentAssignments = form.getValues('departmentAssignments') || [];
    const wasRemovedPrime = currentAssignments[index]?.isPrime;

    const newAssignments = currentAssignments.filter((_, i) => i !== index);

    // If we removed the prime and there are still departments, make the first one prime
    if (wasRemovedPrime && newAssignments.length > 0) {
      newAssignments[0].isPrime = true;
    }

    form.setValue('departmentAssignments', newAssignments, { shouldValidate: true });
  };

  const handleSetPrime = (index: number) => {
    const currentAssignments = form.getValues('departmentAssignments') || [];
    const updatedAssignments = currentAssignments.map((assignment, i) => ({
      ...assignment,
      isPrime: i === index,
    }));
    form.setValue('departmentAssignments', updatedAssignments, { shouldValidate: true });
  };

  const handleSubmit: SubmitHandler<ProjectFormValues> = (data) => {
    onSubmit(data);
  };

  return (
    <FormContext {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Customer/Property Header */}
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-lg font-semibold">
                {`${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim()}
              </p>
              {property && property.address && (
                <p className="text-sm text-gray-600">
                  {property.address}
                  {property.city && `, ${property.city}`} {property.state} {property.zip}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Project Details Form */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Project Details</h2>
            <p className="mt-1 text-gray-600">Provide information about the project</p>
          </div>

          <div className="space-y-6">
            {/* Project Name (auto-generated) */}
            <div>
              <FormLabel>Project Name</FormLabel>
              <p className="mt-1 rounded-md border bg-gray-50 px-3 py-2 text-sm text-gray-600">
                Auto-generated on save
              </p>
            </div>

            {/* Project Manager */}
            <FormField
              control={form.control}
              name="projectManagerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Project Manager</FormLabel>
                  <FormControl>
                    <EmployeeComboboxPicker
                      selected={field.value ? String(field.value) : ''}
                      onSelect={(user) => field.onChange(user?.id || undefined)}
                      renderItem={(user) => `${user.lastname}, ${user.firstname}`}
                      placeholder="Select project manager (optional)"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Start and End Date - Side by Side */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="dateStarted"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel>Start Date</RequiredLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="dateEnded"
                render={({ field }) => (
                  <FormItem>
                    <RequiredLabel>End Date</RequiredLabel>
                    <FormControl>
                      <Input {...field} type="date" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {multiDayFlag && projectDayRange && (
              <Flagged flag="job-creation.multi-day">
                <p className="border-border bg-muted text-foreground rounded-md border px-3 py-2 text-sm font-medium tabular-nums">
                  {projectDayRange}
                </p>
              </Flagged>
            )}

            {/* PO Number */}
            <FormField
              control={form.control}
              name="poNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>PO Number or Other Client Identifier</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="PO number or other client identifier" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Project Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <RequiredLabel>Project Description</RequiredLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Project description..." rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Additional Notes */}
            <FormField
              control={form.control}
              name="logNotes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Any additional notes..." rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Department Selection Section */}
            <div className="space-y-4">
              <div>
                <RequiredLabel>Select Departments/Trades</RequiredLabel>
                <p className="mt-1 text-sm text-gray-500">
                  Add departments that will work on this project. Budget and hours can be set now or
                  adjusted later. The first department is the prime contractor.
                </p>
              </div>

              {/* Department Dropdown and Add Button */}
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <FormLabel className="text-sm">Department</FormLabel>
                  {departmentAssignments.length > 0 && (
                    <Badge variant="outline" className="ml-2 border-blue-600 text-blue-600">
                      Prime Contractor
                    </Badge>
                  )}
                  <Select
                    value={selectedDepartmentId.toString()}
                    onValueChange={(value) => setSelectedDepartmentId(parseInt(value))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Choose department" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableDepartments.map((dept) => (
                        <SelectItem key={dept.id} value={dept.id.toString()}>
                          {dept.description}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Add Department Button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleAddDepartment}
                disabled={selectedDepartmentId === 0}
                className="border-primary text-primary hover:bg-primary/10 w-full"
              >
                Add Department
              </Button>

              {/* Selected Departments List */}
              {departmentAssignments.length > 0 && (
                <div className="space-y-2">
                  {departmentAssignments.map((assignment, index) => (
                    <div
                      key={`${assignment.departmentId}-${index}`}
                      className="rounded-md border border-gray-200 bg-white p-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="font-medium">{assignment.departmentName}</span>
                          {assignment.isPrime && (
                            <Badge className="bg-secondary text-secondary-foreground hover:bg-secondary">
                              Prime Contractor
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {!assignment.isPrime && departmentAssignments.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSetPrime(index)}
                              className="text-xs text-blue-600 hover:text-blue-700"
                            >
                              Set as Prime
                            </Button>
                          )}
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveDepartment(index)}
                          >
                            <TrashIcon className="size-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 rounded-md border border-gray-100 bg-gray-50 p-3">
                        <p className="mb-2 text-xs font-medium text-gray-700">Budget Allocation</p>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <FormLabel className="text-xs text-gray-500">Budget ($)</FormLabel>
                            <Input
                              type="number"
                              min={0}
                              step="0.01"
                              placeholder="0.00"
                              value={
                                assignment.allocatedBudget === 0 ? '' : assignment.allocatedBudget
                              }
                              onChange={(e) => {
                                const updated = [...departmentAssignments];
                                updated[index] = {
                                  ...updated[index],
                                  allocatedBudget:
                                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                                };
                                form.setValue('departmentAssignments', updated);
                              }}
                            />
                          </div>
                          <div>
                            <FormLabel className="text-xs text-gray-500">Hours</FormLabel>
                            <Input
                              type="number"
                              min={0}
                              step="0.5"
                              placeholder="0"
                              value={
                                assignment.allocatedHours === 0 ? '' : assignment.allocatedHours
                              }
                              onChange={(e) => {
                                const updated = [...departmentAssignments];
                                updated[index] = {
                                  ...updated[index],
                                  allocatedHours:
                                    e.target.value === '' ? 0 : parseFloat(e.target.value) || 0,
                                };
                                form.setValue('departmentAssignments', updated);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Error message for departments */}
              {'departmentAssignments' in form.formState.errors &&
                form.formState.errors.departmentAssignments && (
                  <p className="text-sm text-red-600">
                    {(form.formState.errors.departmentAssignments as { message?: string })?.message}
                  </p>
                )}

              {/* Unity Build Integration Info */}
              <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-white p-3">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <InfoCircledIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Unity Build is a construction project management system. You can link this
                        project to Unity Build after creation.
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Unity Build Integration:</span> After creating this
                  project, sync with Unity Build by matching the Kalos job number to your Unity
                  Build contract number.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div
          className={
            compact
              ? 'sticky bottom-0 -mx-6 flex justify-between gap-2 border-t border-gray-200 bg-white px-6 pb-6 pt-4'
              : 'flex justify-between gap-2 pt-4'
          }
        >
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
            {onCancel && !hideCancelButton && (
              <Button type="button" variant="outline" onClick={onCancel}>
                Cancel
              </Button>
            )}
          </div>
          <Button
            type="submit"
            disabled={!form.formState.isValid || loading}
            isLoading={loading}
            className="bg-primary hover:bg-primary/90 text-white"
          >
            Create Project
          </Button>
        </div>
      </form>
    </FormContext>
  );
};
